import React, { useRef } from 'react';
import { l } from 'app/blocks/common/codes';
import { StaticSearchErrorKind } from '../api';

export function useLiveMessage(
    inputValue: string,
    isLoading: boolean,
    isLoadingMore: boolean,
    searchError: StaticSearchErrorKind | null,
    options: readonly Search.Static[],
    focusedItem: string,
): string {
    type Args = {
        focusedItem: string;
        inputValue: string;
        isLoading: boolean;
        isLoadingMore: boolean;
        options: readonly Search.Static[];
        searchError: StaticSearchErrorKind | null;
    };

    const next: Args = { focusedItem, inputValue, isLoading, isLoadingMore, options, searchError };
    const prevRef = useRef<Args>(next);
    const resultRef = useRef('');

    const prev = prevRef.current;
    const changed = (Object.keys(next) as (keyof Args)[]).reduce<Record<keyof Args, boolean>>((acc, k) => {
        acc[k] = next[k] !== prev[k];
        return acc;
    }, {} as Record<keyof Args, boolean>);

    if ((Object.values(changed) as boolean[]).some(Boolean)) {
        prevRef.current = next;
        if (changed.inputValue && inputValue.trim().length < 3) {
            resultRef.current = '';
        } else if (changed.focusedItem && focusedItem) {
            resultRef.current = focusedItem;
        } else if ((changed.isLoading || changed.isLoadingMore) && (isLoading || isLoadingMore)) {
            resultRef.current = l('SEARCH_CODES.STATIC.STATES.LOADING');
        } else if (changed.searchError && searchError === 'connection') {
            resultRef.current = l('SEARCH_CODES.STATIC.STATES.ERROR_CONNECTION');
        } else if (changed.searchError && searchError === 'generic') {
            resultRef.current = l('SEARCH_CODES.STATIC.STATES.ERROR_GENERIC');
        } else if (changed.options && options.length > 0) {
            resultRef.current = `${options.length} results available. Use down arrow to navigate.`;
        }
    }

    return resultRef.current;
}

export const useEnhancedAutocompleteList = (options: readonly Search.Static[]) => {
    const listboxRef = React.useRef<HTMLUListElement | null>(null);
    const highlightIndexRef = React.useRef(-1);

    const setHighlightIndex = React.useCallback((index: number) => {
        highlightIndexRef.current = index;
    }, []);

    React.useEffect(() => {
        const scrollTop = listboxRef.current?.scrollTop ?? 0;

        if (scrollTop > 0) {
            const element = listboxRef.current;
            if (element) element.scrollTop = scrollTop;
        }

        if (highlightIndexRef.current >= 0) {
            const option = listboxRef.current?.querySelector<HTMLElement>(
                `[data-option-index="${highlightIndexRef.current}"]`,
            );
            if (option) {
                option.dispatchEvent(new MouseEvent('mousemove', { bubbles: true }));
            }
        }
    }, [options]);

    return {
        highlightIndex: highlightIndexRef.current,
        listboxRef,
        setHighlightIndex,
    };
};
